import { supabase } from '@/lib/supabase/client';
import type { QuickbooksPurchaseOrderMetadata } from '@/types/quickbooks';

// Helper to get current user ID
async function getCurrentUserId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user?.id ?? null;
}

export interface PurchaseOrder extends QuickbooksPurchaseOrderMetadata {
  id: string;
  po_number: string;
  location_id: string | null;
  launch_id: string | null;
  order_date: string;
  status: 'draft' | 'submitted' | 'approved' | 'purchased' | 'paid' | 'cancelled';
  quickbooks_po_id: string | null;
  quickbooks_sync_date: string | null;
  total_amount: number;
  notes: string | null;
}

export interface PurchaseOrderInsert {
  po_number: string;
  location_id?: string | null;
  launch_id?: string | null;
  order_date: string;
  status?: 'draft' | 'submitted' | 'approved' | 'purchased' | 'paid' | 'cancelled';
  quickbooks_po_id?: string | null;
  quickbooks_sync_date?: string | null;
  quickbooks_po_sync_token?: string | null;
  quickbooks_po_realm_id?: string | null;
  quickbooks_po_environment?: 'sandbox' | 'production' | null;
  quickbooks_doc_number?: string | null;
  quickbooks_po_synced_at?: string | null;
  quickbooks_po_last_error?: string | null;
  total_amount: number;
  notes?: string | null;
}

export interface PurchaseOrderUpdate {
  po_number?: string;
  location_id?: string | null;
  launch_id?: string | null;
  order_date?: string;
  status?: 'draft' | 'submitted' | 'approved' | 'purchased' | 'paid' | 'cancelled';
  quickbooks_po_id?: string | null;
  quickbooks_sync_date?: string | null;
  quickbooks_po_sync_token?: string | null;
  quickbooks_po_realm_id?: string | null;
  quickbooks_po_environment?: 'sandbox' | 'production' | null;
  quickbooks_doc_number?: string | null;
  quickbooks_po_synced_at?: string | null;
  quickbooks_po_last_error?: string | null;
  total_amount?: number;
  notes?: string | null;
}

export const purchaseOrdersService = {
  async getAll(): Promise<PurchaseOrder[]> {
    try {
      const { data, error } = await supabase.from('purchase_orders').select('*').order('order_date', { ascending: false });

      if (error) {
        console.error('Error fetching purchase orders:', error);
        return [];
      }

      return (data as PurchaseOrder[]) || [];
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
      return [];
    }
  },

  async getById(id: string): Promise<PurchaseOrder | null> {
    try {
      const { data, error } = await supabase.from('purchase_orders').select('*').eq('id', id).single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('Error fetching purchase order:', error);
        return null;
      }

      return (data as PurchaseOrder) || null;
    } catch (error) {
      console.error('Error fetching purchase order:', error);
      return null;
    }
  },

  async getByLocationId(locationId: string): Promise<PurchaseOrder[]> {
    try {
      const { data, error } = await supabase
        .from('purchase_orders')
        .select('*')
        .eq('location_id', locationId)
        .order('order_date', { ascending: false });

      if (error) {
        console.error('Error fetching purchase orders by location ID:', error);
        return [];
      }

      return (data as PurchaseOrder[]) || [];
    } catch (error) {
      console.error('Error fetching purchase orders by location ID:', error);
      return [];
    }
  },

  async getByLaunchId(launchId: string): Promise<PurchaseOrder[]> {
    try {
      const { data, error } = await supabase
        .from('purchase_orders')
        .select('*')
        .eq('launch_id', launchId)
        .order('order_date', { ascending: false });

      if (error) {
        console.error('Error fetching purchase orders by launch ID:', error);
        return [];
      }

      return (data as PurchaseOrder[]) || [];
    } catch (error) {
      console.error('Error fetching purchase orders by launch ID:', error);
      return [];
    }
  },

  async create(data: PurchaseOrderInsert): Promise<PurchaseOrder> {
    try {
      const userId = await getCurrentUserId();

      const insertData = {
        po_number: data.po_number,
        location_id: data.location_id ?? null,
        launch_id: data.launch_id ?? null,
        order_date: data.order_date,
        status: data.status ?? 'draft',
        total_amount: data.total_amount,
        notes: data.notes ?? null,
        created_by: userId,
      };

      const { data: newPO, error } = await supabase.from('purchase_orders').insert(insertData).select().single();

      if (error) {
        console.error('Error creating purchase order:', error);
        if (error.code === '23505' && error.message.includes('purchase_orders_po_number_key')) {
          throw new Error('A purchase order with this PO number already exists. Open it from Purchase Orders, or use a different PO number for a separate purchase.');
        }
        throw new Error(`Failed to create purchase order: ${error.message}`);
      }

      return newPO as PurchaseOrder;
    } catch (error) {
      console.error('Error creating purchase order:', error);
      throw error;
    }
  },

  async update(id: string, data: PurchaseOrderUpdate): Promise<PurchaseOrder> {
    try {
      const updateData: Record<string, unknown> = {};

      // Only include fields that are provided
      if (data.po_number !== undefined) updateData.po_number = data.po_number;
      if (data.location_id !== undefined) updateData.location_id = data.location_id;
      if (data.launch_id !== undefined) updateData.launch_id = data.launch_id;
      if (data.order_date !== undefined) updateData.order_date = data.order_date;
      if (data.status !== undefined) updateData.status = data.status;
      if (data.total_amount !== undefined) updateData.total_amount = data.total_amount;
      if (data.notes !== undefined) updateData.notes = data.notes;

      const { data: updatedPO, error } = await supabase
        .from('purchase_orders')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating purchase order:', error);
        throw new Error(`Failed to update purchase order: ${error.message}`);
      }

      if (!updatedPO) {
        throw new Error('Purchase order not found');
      }

      return updatedPO as PurchaseOrder;
    } catch (error) {
      console.error('Error updating purchase order:', error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      const { error } = await supabase.from('purchase_orders').delete().eq('id', id);

      if (error) {
        console.error('Error deleting purchase order:', error);
        throw new Error(`Failed to delete purchase order: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting purchase order:', error);
      throw error;
    }
  },
};
